import React from 'react';
import splashImage from '../images/splashscreen.png';

const SplashScreen = ({ onNewGame, onShowHistory, onLoadGame, onShowPlayerPerformance, onManagePlayers, isGameInProgress, onResumeGame, onLogout, mostRecentPausedGame }) => {
    return (
        <div className="min-h-screen w-full bg-[#1a2332] flex flex-col items-center justify-between p-4 relative overflow-y-auto font-['Inter']">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5 pointer-events-none">
                <div className="absolute top-0 left-0 w-full h-full"
                    style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 5 C 35 15 45 15 45 25 C 45 35 30 50 30 50 C 30 50 15 35 15 25 C 15 15 25 15 30 5 Z' fill='%23ffffff' fill-rule='evenodd'/%3E%3C/svg%3E")`,
                        backgroundSize: '60px 60px'
                    }}
                />
            </div>

            {/* Ambient Glow */}
            <div className="absolute top-1/4 left-1/4 w-[40vh] h-[40vh] bg-purple-500/20 rounded-full blur-[100px] -translate-x-1/2 -translate-y-1/2" />
            <div className="absolute bottom-1/4 right-1/4 w-[40vh] h-[40vh] bg-blue-500/20 rounded-full blur-[100px] translate-x-1/2 translate-y-1/2" />

            {/* Spacer for top */}
            <div className="flex-grow" />

            {/* Main Content - Centered with flex */}
            <main className="z-10 flex flex-col items-center justify-center w-full max-w-md space-y-4 sm:space-y-6 flex-shrink-0">
                {/* Logo Section */}
                <div className="flex flex-col items-center space-y-3 sm:space-y-4">
                    {/* Image without background container */}
                    <div className="relative w-[40vw] h-[40vw] max-w-[220px] max-h-[220px] min-w-[140px] min-h-[140px] sm:w-[50vw] sm:h-[50vw] sm:max-w-[280px] sm:max-h-[280px] sm:min-w-[180px] sm:min-h-[180px] flex items-center justify-center transform hover:scale-105 transition-transform duration-500">
                        <img src={splashImage} alt="Cutthroat Spades" className="w-full h-full object-contain drop-shadow-2xl filter brightness-110" />
                    </div>

                    <div className="text-center space-y-1 sm:space-y-2">
                        <h1 className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-yellow-400 to-amber-200 drop-shadow-sm font-['Roboto_Slab']">
                            Spades Scorecard
                        </h1>
                        <p className="text-xs sm:text-sm md:text-base text-gray-400 font-medium px-4">
                            The ultimate companion for your game night
                        </p>
                    </div>
                </div>

                {/* Primary Actions */}
                <div className="w-full space-y-2 sm:space-y-3 px-6 sm:px-8">
                    {isGameInProgress && (
                        <button
                            onClick={onResumeGame}
                            className="w-full group relative overflow-hidden rounded-2xl bg-green-600 p-2.5 sm:p-3 transition-all hover:bg-green-500 hover:shadow-lg hover:shadow-green-500/30 active:scale-95"
                        >
                            <div className="relative z-10 flex items-center justify-center space-x-2">
                                <div className="flex flex-col items-center">
                                    <span className="text-sm sm:text-base font-bold text-white">Resume Game</span>
                                    {mostRecentPausedGame && (
                                        <span className="text-[10px] sm:text-xs font-medium text-green-100">
                                            {new Date(mostRecentPausedGame.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} • {new Date(mostRecentPausedGame.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                    )}
                                </div>
                                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </button>
                    )}

                    <button
                        onClick={onNewGame}
                        className="w-full group relative overflow-hidden rounded-2xl bg-gradient-to-r from-amber-500 to-orange-600 p-2.5 sm:p-3 transition-all hover:from-amber-400 hover:to-orange-500 hover:shadow-lg hover:shadow-orange-500/30 active:scale-95"
                    >
                        <div className="relative z-10 flex items-center justify-center space-x-2">
                            <span className="text-sm sm:text-base font-bold text-white">New Game</span>
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white transition-transform group-hover:rotate-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                        </div>
                    </button>
                </div>
            </main>

            {/* Spacer for bottom - flexible */}
            <div className="flex-grow" />

            {/* Secondary Actions Footer */}
            <footer className="w-full max-w-md px-6 pb-6 z-20 flex-shrink-0">
                <div className="grid grid-cols-4 gap-2 sm:gap-3 mb-4 sm:mb-6">
                    <button
                        onClick={onLoadGame}
                        className="flex flex-col items-center justify-center space-y-1 p-2 rounded-xl hover:bg-white/5 transition-colors group"
                    >
                        <div className="p-2 rounded-full bg-gray-800 group-hover:bg-gray-700 transition-colors">
                            <svg className="w-5 h-5 text-gray-400 group-hover:text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                            </svg>
                        </div>
                        <span className="text-[10px] sm:text-xs font-medium text-gray-500 group-hover:text-gray-300">Paused Games</span>
                    </button>

                    <button
                        onClick={onShowHistory}
                        className="flex flex-col items-center justify-center space-y-1 p-2 rounded-xl hover:bg-white/5 transition-colors group"
                    >
                        <div className="p-2 rounded-full bg-gray-800 group-hover:bg-gray-700 transition-colors">
                            <svg className="w-5 h-5 text-gray-400 group-hover:text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <span className="text-[10px] sm:text-xs font-medium text-gray-500 group-hover:text-gray-300">History</span>
                    </button>

                    <button
                        onClick={onShowPlayerPerformance}
                        className="flex flex-col items-center justify-center space-y-1 p-2 rounded-xl hover:bg-white/5 transition-colors group"
                    >
                        <div className="p-2 rounded-full bg-gray-800 group-hover:bg-gray-700 transition-colors">
                            <svg className="w-5 h-5 text-gray-400 group-hover:text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                        </div>
                        <span className="text-[10px] sm:text-xs font-medium text-gray-500 group-hover:text-gray-300">Leaderboard</span>
                    </button>

                    <button
                        onClick={onManagePlayers}
                        className="flex flex-col items-center justify-center space-y-1 p-2 rounded-xl hover:bg-white/5 transition-colors group"
                    >
                        <div className="p-2 rounded-full bg-gray-800 group-hover:bg-gray-700 transition-colors">
                            <svg className="w-5 h-5 text-gray-400 group-hover:text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                        </div>
                        <span className="text-[10px] sm:text-xs font-medium text-gray-500 group-hover:text-gray-300">Players</span>
                    </button>
                </div>

                <button
                    onClick={onLogout}
                    className="w-full py-2 text-xs font-semibold text-red-500/70 hover:text-red-400 transition-colors flex items-center justify-center space-x-1"
                >
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <span>Sign Out</span>
                </button>
            </footer>
        </div>
    );
};

export default SplashScreen;
